/* istanbul ignore file */

import { HandlerEvent } from 'post-robot';
import { ValidationError } from 'common/errors';
import { FlowState, saveFlow, saveUser, UserProfile } from 'communicator/store';
import { getIntegrationType, getIsVisaSRCi, getLaunchData } from 'communicator/store/selectors';
import { ToRoute } from 'types/data';
import { GoToInitiator } from 'types/messages/incoming';
import { CheckoutLaunchData, CheckoutPayload } from 'types/visa-sdk/checkout';

export type LaunchSRCiRequest = {
  data: GoToInitiator['data'];
  closeCheckoutWindow(): void;
  launchSRCi(launchData: CheckoutLaunchData): void;
};

export default function launchSrci(
  event: HandlerEvent<LaunchSRCiRequest>
): CheckoutPayload | ValidationError {
  const dcfData = event.data.data;

  const flowUpdates: Partial<FlowState> = {
    // VCO-15459 maintain grant on Vsb switch card
    grant: dcfData.customData.grant
  };

  const userProfileUpdates: Partial<UserProfile> = {
    country: dcfData.customData.countryCode
  };

  const { flow, why } = dcfData;

  let checkoutError: { message: string; reason: string } | null = null;

  if (flow === 'identity') {
    flowUpdates.dcfActionCode = 'SWITCH_CONSUMER';
    flowUpdates.toRoute = 'identity';
  } else if (flow === 're-enter-card') {
    flowUpdates.dcfActionCode = 'SWITCH_CONSUMER';
    flowUpdates.toRoute = 're-enter-card';
  } else if (flow === 'add-card') {
    flowUpdates.toRoute = 'add-card';
  } else if (flow === 'otp' && why === 'dcf:session-timeout:reset-app') {
    flowUpdates.dcfActionCode = 'SWITCH_CONSUMER';
    flowUpdates.toRoute = 'otp';
  } else if (flow === 'account-locked') {
    flowUpdates.dcfActionCode = 'CHANGE_CARD';
    flowUpdates.toRoute = 'card-list';
    flowUpdates.why = 'ACCT_INACCESSIBLE';

    userProfileUpdates.accessToken = null;
    userProfileUpdates.xAuthCode = undefined;
  } else if (why === 'add-card') {
    // DCF add cards should land on the card list screen
    flowUpdates.dcfActionCode = 'ADD_CARD';
  } else if (why === 'dcf:get-intent:failed') {
    flowUpdates.dcfActionCode = 'ERROR';
    checkoutError = {
      message: 'Unable to connect to DCF',
      reason: 'UNABLE_TO_CONNECT'
    };
  } else if (why === 'dcf:edit-card:delete') {
    flowUpdates.dcfActionCode = 'CHANGE_CARD';
    flowUpdates.toRoute = 'card-list';
    flowUpdates.why = 'dcf:edit-card:delete';
  } else if (why === 'device-locked') {
    flowUpdates.dcfActionCode = 'CHANGE_CARD';
    flowUpdates.why = 'ACCT_INACCESSIBLE';
    // 'flow' will be either RE_ENTER_CARD or CARD_LIST, based on if the user is in Card First Flow or not.
    flowUpdates.toRoute = flow as ToRoute;

    userProfileUpdates.accessToken = null;
    userProfileUpdates.xAuthCode = undefined;
  } else if (why === 'ERROR') {
    flowUpdates.toRoute = flow as ToRoute;
    flowUpdates.dcfActionCode = 'ERROR';

    checkoutError = {
      message: 'Unable to connect to DCF',
      reason: 'UNABLE_TO_CONNECT'
    };
    // begin handling dcf integrated add card errors
  } else if (
    why === 'dcf:add-card-failed:accountlock' ||
    why === 'dcf:add-card-failed:apifailure' ||
    why === 'dcf:add-card-failed:missingfield' ||
    why === 'dcf:add-card-failed:privacy' ||
    why === 'dcf:add-card-failed:terms'
  ) {
    const DCF_INTEGRATED_ERROR_MAP: Record<string, string | undefined> = {
      'dcf:add-card-failed:accountlock': 'Something went wrong! User account is locked',
      'dcf:add-card-failed:apifailure':
        'Something went wrong! Failed to add card, please check later'
    };

    checkoutError = {
      message:
        DCF_INTEGRATED_ERROR_MAP[why] ??
        'One or more required parameter missing, please check missing fields',
      reason: 'CARD_ADD_FAILED'
    };
  }

  saveFlow(flowUpdates);
  saveUser(userProfileUpdates);

  const integrationType = getIntegrationType();

  if (getIsVisaSRCi() && integrationType !== 'vsb-buttonless') {
    event.data.launchSRCi(getLaunchData());
  } else {
    event.data.closeCheckoutWindow();
  }

  if (checkoutError) {
    return {
      error: checkoutError
    };
  }

  return {
    dcfActionCode: flowUpdates.dcfActionCode ?? 'CHANGE_CARD',
    idToken: dcfData.idToken
  };
}
